import { useState, useEffect } from "react";
import { useParams, Link, Navigate } from "react-router-dom";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import ProductFAQ from "@/pages/products/components/ProductFAQ";
import { getProductBySlug, getProducts, ProductDetail } from "@/services/productService";

export default function ProductDetail() {
  const { productId } = useParams<{ productId: string }>();
  const [product, setProduct] = useState<ProductDetail | null>(null);
  const [otherProducts, setOtherProducts] = useState<ProductDetail[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    async function load() {
      if (!productId) return;
      try {
        setLoading(true);
        setError(null);
        const [prod, allProds] = await Promise.all([
          getProductBySlug(productId),
          getProducts(),
        ]);
        if (!prod) {
          setNotFound(true);
          return;
        }
        setProduct(prod);
        setOtherProducts(allProds.filter((p) => p.id !== prod.id).slice(0, 4));
      } catch (err) {
        setError("Failed to load product details.");
      } finally {
        setLoading(false);
      }
    }
    load();
  }, [productId]);

  if (notFound) {
    return <Navigate to="/products" replace />;
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-background-50 flex items-center justify-center">
        <div className="w-10 h-10 border-2 border-primary-300 border-t-primary-500 rounded-full animate-spin"></div>
      </div>
    );
  }

  if (error || !product) {
    return (
      <div className="min-h-screen bg-background-50 flex flex-col items-center justify-center">
        <p className="text-foreground-600 mb-4">{error || "Product not found"}</p>
        <Link
          to="/products"
          className="px-5 py-2 rounded-full bg-primary-500 text-background-50 text-sm font-semibold hover:bg-primary-600 transition-colors"
        >
          Back to Products
        </Link>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background-50">
      <Navbar />

      {/* JSON-LD Product Schema */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Product",
            name: product.title,
            description: product.description,
            image: product.heroImage,
            brand: {
              "@type": "Brand",
              name: "Ezzogenics",
            },
            offers: {
              "@type": "Offer",
              availability: "https://schema.org/InStock",
              priceCurrency: "SGD",
              seller: {
                "@type": "Organization",
                name: "Ezzogenics",
              },
            },
          }),
        }}
      />

      <main>
        {/* Hero */}
        <section className="relative pt-20 min-h-[420px] md:min-h-[520px] flex items-end">
          <div className="absolute inset-0">
            <img
              src={product.heroImage}
              alt={`${product.title} Singapore`}
              title={`${product.title} - Ezzogenics`}
              className="w-full h-full object-cover object-top"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-foreground-950/80 via-foreground-950/40 to-foreground-950/20"></div>
          </div>
          <div className="relative z-10 w-full px-4 md:px-6 lg:px-10 py-10 md:py-14 max-w-7xl mx-auto">
            {/* Breadcrumb */}
            <nav aria-label="Breadcrumb" className="mb-4">
              <ol className="flex items-center gap-2 text-xs text-background-300">
                <li><Link to="/" className="hover:text-background-50 transition-colors">Home</Link></li>
                <li><i className="ri-arrow-right-s-line"></i></li>
                <li><Link to="/products" className="hover:text-background-50 transition-colors">Products</Link></li>
                <li><i className="ri-arrow-right-s-line"></i></li>
                <li className="text-background-50 font-medium">{product.shortTitle}</li>
              </ol>
            </nav>
            <span className="inline-block text-xs font-semibold bg-primary-500 text-background-50 rounded-full px-3 py-1 mb-4">
              {product.category}
            </span>
            <h1 className="font-heading text-3xl md:text-4xl lg:text-5xl font-bold text-background-50 leading-tight mb-4 max-w-3xl">
              {product.title} <span className="text-primary-400">Singapore</span>
            </h1>
            <p className="text-background-200 text-base md:text-lg max-w-2xl leading-relaxed">
              {product.summary}
            </p>
          </div>
        </section>

        {/* Overview + Specs */}
        <section className="px-4 md:px-6 lg:px-10 py-12 md:py-16 max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 lg:gap-12">
            {/* Main Content */}
            <div className="lg:col-span-2 flex flex-col gap-8">
              {/* Description */}
              <div>
                <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 mb-4">
                  About {product.shortTitle}
                </h2>
                <p className="text-foreground-600 text-base leading-relaxed mb-4">
                  {product.description}
                </p>
                <p className="text-foreground-600 text-base leading-relaxed">
                  At Ezzogenics, we offer the best selection of industry-standard{" "}
                  <strong className="text-foreground-900">{product.title.toLowerCase()} Singapore</strong>. Our SCDF-certified
                  consultants will help you choose the right system for your premises and budget,
                  and our professional team handles installation, testing, and maintenance.
                </p>
              </div>

              {/* Features */}
              <div>
                <h3 className="font-heading text-xl font-bold text-foreground-950 mb-4">
                  Key Features
                </h3>
                <ul className="flex flex-col gap-3">
                  {product.features.map((feature, i) => (
                    <li key={i} className="flex items-start gap-3">
                      <div className="w-6 h-6 flex items-center justify-center rounded-full bg-primary-100 flex-shrink-0 mt-0.5">
                        <i className="ri-check-line text-primary-600 text-xs"></i>
                      </div>
                      <span className="text-foreground-700 text-sm leading-relaxed">{feature}</span>
                    </li>
                  ))}
                </ul>
              </div>

              {/* Types */}
              {product.types && product.types.length > 0 && (
                <div>
                  <h3 className="font-heading text-xl font-bold text-foreground-950 mb-5">
                    Types of {product.shortTitle}
                  </h3>
                  <div className="flex flex-col gap-4">
                    {product.types.map((type, i) => (
                      <div key={i} className="bg-background-100 rounded-xl p-5 border border-background-200/70">
                        <h4 className="font-semibold text-foreground-900 text-base mb-2">
                          <a href="#product-types" className="hover:text-primary-500 transition-colors">
                            {type.name}
                          </a>
                        </h4>
                        <p className="text-foreground-600 text-sm leading-relaxed">{type.description}</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Applications */}
              <div>
                <h3 className="font-heading text-xl font-bold text-foreground-950 mb-4">
                  Applications
                </h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {product.applications.map((app, i) => (
                    <div key={i} className="flex items-center gap-3 bg-background-100 rounded-lg px-4 py-3 border border-background-200/70">
                      <i className="ri-building-line text-accent-500 text-base flex-shrink-0"></i>
                      <span className="text-foreground-700 text-sm">{app}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Sidebar */}
            <div className="flex flex-col gap-5">
              {/* Spec Table */}
              <div className="bg-background-100 rounded-2xl p-6 border border-background-200/70 sticky top-24">
                <h3 className="font-heading text-base font-bold text-foreground-950 mb-4">
                  Product Specifications
                </h3>
                <div className="flex flex-col gap-3">
                  {product.specs.map((spec, i) => (
                    <div key={i} className="flex items-start justify-between gap-3 pb-3 border-b border-background-200/70 last:border-0 last:pb-0">
                      <span className="text-foreground-500 text-xs font-medium uppercase tracking-wide">{spec.label}</span>
                      <span className="text-foreground-900 text-sm font-semibold text-right">{spec.value}</span>
                    </div>
                  ))}
                </div>

                <div className="mt-6 flex flex-col gap-3">
                  <Link
                    to="/contact"
                    className="inline-flex items-center justify-center gap-2 px-5 py-3 rounded-full bg-primary-500 text-background-50 text-sm font-semibold hover:bg-primary-600 transition-colors w-full whitespace-nowrap"
                  >
                    Request a Quote
                    <i className="ri-arrow-right-line"></i>
                  </Link>
                  <Link
                    to="/contact"
                    className="inline-flex items-center justify-center gap-2 px-5 py-3 rounded-full border border-primary-300 text-primary-600 text-sm font-semibold hover:bg-primary-50 transition-colors w-full whitespace-nowrap"
                  >
                    Upload Documents
                    <i className="ri-upload-cloud-2-line"></i>
                  </Link>
                </div>
              </div>

              {/* Trust Signals */}
              <div className="bg-accent-50 rounded-2xl p-5 border border-accent-100">
                <h4 className="font-semibold text-foreground-900 text-sm mb-3">Why Choose Us</h4>
                <ul className="flex flex-col gap-2.5">
                  {[
                    "SCDF-certified installation team",
                    "Industry-standard products",
                    "Professional testing & maintenance",
                    "Singapore SS CP 10 compliant",
                    "Trusted by 500+ clients",
                  ].map((item) => (
                    <li key={item} className="flex items-center gap-2 text-xs text-foreground-700">
                      <i className="ri-shield-check-line text-accent-500 text-sm"></i>
                      {item}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </section>

        {/* FAQ */}
        <ProductFAQ faqs={product.faqs} productTitle={product.shortTitle} />

        {/* Related Products */}
        <section className="px-4 md:px-6 lg:px-10 py-12 md:py-16 max-w-7xl mx-auto">
          <div className="flex items-center justify-between gap-4 mb-8">
            <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950">
              Related <span className="text-primary-500">Products</span>
            </h2>
            <Link
              to="/products"
              className="text-sm font-semibold text-primary-500 hover:text-primary-600 transition-colors flex items-center gap-1 whitespace-nowrap"
            >
              View all <i className="ri-arrow-right-line"></i>
            </Link>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {otherProducts.map((p) => (
              <Link
                key={p.id}
                to={`/products/${p.slug}`}
                className="group bg-background-50 rounded-2xl border border-background-200/70 overflow-hidden hover:border-primary-300 transition-all block"
              >
                <div className="h-40 overflow-hidden">
                  <img
                    src={p.cardImage}
                    alt={`${p.title} Singapore`}
                    className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
                    loading="lazy"
                  />
                </div>
                <div className="p-4">
                  <p className="text-xs text-foreground-500 mb-1">{p.category}</p>
                  <h3 className="font-heading text-sm font-bold text-foreground-950 group-hover:text-primary-600 transition-colors leading-snug">
                    {p.title}
                  </h3>
                </div>
              </Link>
            ))}
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}